import { useState, useEffect, useCallback, useMemo } from "react";
import { Search, ArrowUpDown, ShoppingCart, IndianRupee, Copy, ListFilter, X, Check, ChevronsUpDown, TrendingUp, Download, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DatePickerField } from "@/components/ui/date-picker";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Shimmer, TableShimmer } from "@/components/ui/shimmer";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DataTablePagination } from "@/components/ui/data-table-pagination";
import { useAuth } from "@/context/AuthContext";
import { graphqlRequest } from "@/utils/graphql";
import { exportCustomerTransactionsToExcel } from "@/utils/export-excel";

/* ── Types ── */

/** Wire shape returned by the `getCustomerTransactions` Hasura action.
 *  `service_name`, `service_image`, `external_id`, `earnings_paise` are
 *  enriched server-side from coupon_allocation + commission_map joins —
 *  null for wallet rows that aren't `buy_debit` purchases or that have
 *  no matching allocation. */
type WalletTransaction = {
  id: string;
  amount: number;
  type: string;
  purpose: string;
  created_at: string;
  payment_id: string | null;
  auth: {
    fullname: string;
    phone: string;
  };
  service_name: string | null;
  service_image: string | null;
  external_id: string | null;
  earnings_paise: number | null;
};

type SortOption = "newest" | "oldest" | "amount-high" | "amount-low";

/* ── Query ── */

const FETCH_CUSTOMER_TRANSACTIONS = `
  query GetCustomerTransactions($user_id: uuid!, $limit: Int!, $offset: Int!) {
    getCustomerTransactions(request: { user_id: $user_id, limit: $limit, offset: $offset }) {
      transactions {
        id
        amount
        type
        purpose
        created_at
        payment_id
        auth {
          fullname
          phone
        }
        service_name
        service_image
        external_id
        earnings_paise
      }
      total
      total_purchases
      purchase_volume
    }
  }
`;

/* ── Helpers ── */

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatINR(paise: number): string {
  const rupees = Math.abs(paise) / 100;
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: rupees % 1 !== 0 ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(rupees);
}

const typeBadge: Record<string, { label: string; className: string }> = {
  buy_debit: { label: "Purchase", className: "border-blue-500 text-blue-600 dark:text-blue-400" },
  paid_via_phonepe: { label: "PhonePe", className: "border-purple-500 text-purple-600 dark:text-purple-400" },
  paid_via_upi: { label: "UPI", className: "border-green-500 text-green-600 dark:text-green-400" },
  phonepe_refund: { label: "Refund", className: "border-red-500 text-red-500 dark:text-red-400" },
  refund: { label: "Refund", className: "border-red-500 text-red-500 dark:text-red-400" },
  credit: { label: "Credit", className: "border-green-500 text-green-600 dark:text-green-400" },
  debit: { label: "Debit", className: "border-orange-500 text-orange-600 dark:text-orange-400" },
  // Synthetic type emitted by getCustomerTransactions for b2b shared-sub
  // buys (which don't write to whatsub_wallet). Rendered identically to a
  // brand purchase so the dashboard treats them uniformly.
  subscription_buy: { label: "Subscription", className: "border-pink-500 text-pink-600 dark:text-pink-400" },
};

function getTypeBadge(type: string) {
  return typeBadge[type] ?? { label: type.replace(/_/g, " "), className: "border-gray-400 text-gray-500" };
}

/* ── Component ── */

export default function CustomerTransactionsPage() {
  const { userId, token } = useAuth();

  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [stats, setStats] = useState({ transactions: 0, purchaseCount: 0, purchaseVolume: 0 });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<SortOption>("newest");

  // Excel export state — spinner while the full dataset is being fetched.
  const [exporting, setExporting] = useState(false);
  const EXPORT_MAX_ROWS = 20000; // matches admin panel's cap

  // Filters (client-side, applied to the loaded page). Server-side filters
  // need a Hasura action input change; we'll layer those in once the action
  // schema is updated. Keeps this PR fully additive.
  const [showFilters, setShowFilters] = useState(false);
  const [filterFrom, setFilterFrom] = useState<Date | undefined>(undefined);
  const [filterTo, setFilterTo] = useState<Date | undefined>(undefined);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedBrandIds, setSelectedBrandIds] = useState<string[]>([]);
  const [brandPickerOpen, setBrandPickerOpen] = useState(false);

  // Single source of truth — the action handler enriches each transaction
  // server-side (brand, external_id, earnings) so the dashboard just renders
  // what comes back. No timestamp matching, no permission cascade.
  const fetchData = useCallback(async () => {
    if (!userId || !token) return;
    setLoading(true);
    setError("");
    try {
      const data = await graphqlRequest<{
        getCustomerTransactions: {
          transactions: WalletTransaction[];
          total: number;
          total_purchases: number;
          purchase_volume: number;
        };
      }>(FETCH_CUSTOMER_TRANSACTIONS, {
        user_id: userId,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      }, token);

      const result = data.getCustomerTransactions;
      setTransactions(result.transactions);
      setTotal(result.total);
      setStats({
        transactions: result.total,
        purchaseCount: result.total_purchases,
        purchaseVolume: result.purchase_volume,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load transactions");
    } finally {
      setLoading(false);
    }
  }, [userId, token, page, rowsPerPage]);

  useEffect(() => { fetchData(); }, [fetchData]);

  /** Fetch every transaction (bypasses page limit) and download as .xlsx.
   *  Applies the currently active client-side filters (search + date range +
   *  type + brand) so the export mirrors what the user is looking at, just
   *  without pagination. Sort is respected too. */
  const handleExport = useCallback(async () => {
    if (!userId || !token || exporting) return;
    setExporting(true);
    setError("");
    try {
      const data = await graphqlRequest<{
        getCustomerTransactions: { transactions: WalletTransaction[] };
      }>(FETCH_CUSTOMER_TRANSACTIONS, {
        user_id: userId,
        limit: EXPORT_MAX_ROWS,
        offset: 0,
      }, token);

      let rows = data.getCustomerTransactions.transactions;

      // Same filter chain as the on-screen table — kept inline (rather than
      // reusing the `filtered` memo) because `filtered` only covers the
      // currently-loaded page, not the full dataset we just fetched.
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        rows = rows.filter((t) =>
          t.auth.fullname.toLowerCase().includes(q) ||
          t.auth.phone.toLowerCase().includes(q) ||
          t.purpose.toLowerCase().includes(q) ||
          t.type.toLowerCase().includes(q) ||
          (t.service_name?.toLowerCase().includes(q) ?? false) ||
          (t.external_id?.toLowerCase().includes(q) ?? false),
        );
      }
      if (filterFrom) {
        const from = new Date(filterFrom);
        from.setHours(0, 0, 0, 0);
        const fromMs = from.getTime();
        rows = rows.filter((t) => new Date(t.created_at).getTime() >= fromMs);
      }
      if (filterTo) {
        const to = new Date(filterTo);
        to.setHours(23, 59, 59, 999);
        const toMs = to.getTime();
        rows = rows.filter((t) => new Date(t.created_at).getTime() <= toMs);
      }
      if (selectedTypes.length > 0) {
        const set = new Set(selectedTypes);
        rows = rows.filter((t) => set.has(t.type));
      }
      if (selectedBrandIds.length > 0) {
        const set = new Set(selectedBrandIds);
        rows = rows.filter((t) => (t.service_name ? set.has(t.service_name) : false));
      }
      rows = [...rows].sort((a, b) => {
        switch (sort) {
          case "newest": return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
          case "oldest": return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          case "amount-high": return Math.abs(b.amount) - Math.abs(a.amount);
          case "amount-low": return Math.abs(a.amount) - Math.abs(b.amount);
        }
      });

      const stamp = new Date().toISOString().slice(0, 10);
      exportCustomerTransactionsToExcel(rows, `customer-transactions-${stamp}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Export failed");
    } finally {
      setExporting(false);
    }
  }, [userId, token, exporting, search, sort, filterFrom, filterTo, selectedTypes, selectedBrandIds]);

  // Brand list available in the currently-loaded page — feeds the brand
  // multi-select picker. Sorted alphabetically. Uses service_name as the
  // key (no service_id surfaced to client; keeps the response slim).
  const availableBrands = useMemo(() => {
    const seen = new Set<string>();
    for (const t of transactions) {
      if (t.service_name) seen.add(t.service_name);
    }
    return Array.from(seen).sort((a, b) => a.localeCompare(b));
  }, [transactions]);

  // Available types in the loaded page. We don't hard-code the list — the
  // backend may emit new wallet types over time and this picker stays current.
  const availableTypes = useMemo(() => {
    const set = new Set<string>();
    for (const t of transactions) set.add(t.type);
    return Array.from(set).sort();
  }, [transactions]);

  const hasActiveFilters =
    !!filterFrom || !!filterTo || selectedTypes.length > 0 || selectedBrandIds.length > 0;

  const clearFilters = () => {
    setFilterFrom(undefined);
    setFilterTo(undefined);
    setSelectedTypes([]);
    setSelectedBrandIds([]);
  };

  // Client-side search + filter + sort. All inputs are read straight off
  // the transaction (server-enriched), so no derived lookups are needed.
  const filtered = useMemo(() => {
    let result = transactions;

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter((t) =>
        t.auth.fullname.toLowerCase().includes(q) ||
        t.auth.phone.toLowerCase().includes(q) ||
        t.purpose.toLowerCase().includes(q) ||
        t.type.toLowerCase().includes(q) ||
        (t.service_name?.toLowerCase().includes(q) ?? false) ||
        (t.external_id?.toLowerCase().includes(q) ?? false),
      );
    }

    if (filterFrom) {
      const from = new Date(filterFrom);
      from.setHours(0, 0, 0, 0);
      const fromMs = from.getTime();
      result = result.filter((t) => new Date(t.created_at).getTime() >= fromMs);
    }
    if (filterTo) {
      const to = new Date(filterTo);
      to.setHours(23, 59, 59, 999);
      const toMs = to.getTime();
      result = result.filter((t) => new Date(t.created_at).getTime() <= toMs);
    }
    if (selectedTypes.length > 0) {
      const set = new Set(selectedTypes);
      result = result.filter((t) => set.has(t.type));
    }
    if (selectedBrandIds.length > 0) {
      const set = new Set(selectedBrandIds);
      result = result.filter((t) => (t.service_name ? set.has(t.service_name) : false));
    }

    result = [...result].sort((a, b) => {
      switch (sort) {
        case "newest": return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        case "oldest": return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        case "amount-high": return Math.abs(b.amount) - Math.abs(a.amount);
        case "amount-low": return Math.abs(a.amount) - Math.abs(b.amount);
      }
    });
    return result;
  }, [transactions, search, sort, filterFrom, filterTo, selectedTypes, selectedBrandIds]);

  /** Earnings rolled up across the currently-filtered rows — gives the
   *  client a quick "how much did I make on what's visible" reading. */
  const filteredEarnings = useMemo(() => {
    let total = 0;
    for (const t of filtered) {
      if (t.earnings_paise != null) total += t.earnings_paise;
    }
    return total;
  }, [filtered]);

  const statCards = [
    { label: "Total Transactions", value: stats.transactions.toLocaleString("en-IN"), icon: <ArrowUpDown className="h-4 w-4" /> },
    { label: "Purchases", value: stats.purchaseCount.toLocaleString("en-IN"), icon: <ShoppingCart className="h-4 w-4" /> },
    { label: "Purchase Volume", value: formatINR(stats.purchaseVolume), icon: <IndianRupee className="h-4 w-4" /> },
    { label: "Your Earnings", value: formatINR(filteredEarnings), icon: <TrendingUp className="h-4 w-4" /> },
  ];

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-1">Customer Transactions</h1>
        <p className="text-muted-foreground">
          View all transactions made by your SDK users.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {statCards.map((s) => (
          <Card key={s.label}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                {s.icon}
                <span className="text-xs font-medium">{s.label}</span>
              </div>
              <p className="text-xl font-bold">{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between flex-wrap gap-4 mb-4">
        <span className="text-sm text-muted-foreground font-medium">
          {filtered.length === transactions.length
            ? `${total} transaction${total !== 1 ? "s" : ""}`
            : `${filtered.length} of ${transactions.length} matching`}
        </span>
        <div className="flex gap-3 items-center">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search user, brand, purpose..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 w-[240px]"
            />
          </div>
          <Button
            variant={showFilters || hasActiveFilters ? "default" : "ghost"}
            size="icon"
            onClick={() => setShowFilters((p) => !p)}
            aria-label="Toggle filters"
          >
            <ListFilter className="h-4 w-4" />
          </Button>
          <Select value={sort} onValueChange={(v) => setSort(v as SortOption)}>
            <SelectTrigger className="w-[160px]">
              <ArrowUpDown className="h-4 w-4 text-muted-foreground mr-1.5" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest First</SelectItem>
              <SelectItem value="oldest">Oldest First</SelectItem>
              <SelectItem value="amount-high">Highest Amount</SelectItem>
              <SelectItem value="amount-low">Lowest Amount</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={exporting || total === 0}
            className="gap-1.5"
            title="Download all transactions matching current filters as Excel"
          >
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {exporting ? "Exporting..." : "Export Excel"}
          </Button>
        </div>
      </div>

      {/* Filter panel */}
      <Collapsible open={showFilters} onOpenChange={setShowFilters}>
        <CollapsibleContent>
          <Card className="mb-4">
            <CardContent className="p-4 space-y-4">
              {/* 4-col grid on md+: date range gets 2 cols (needs both From
                  and To pickers visible), type and brand take 1 each. */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Date range */}
                <div className="md:col-span-2">
                  <p className="text-xs font-semibold text-muted-foreground mb-2">Date range</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <DatePickerField value={filterFrom} onChange={setFilterFrom} placeholder="From" />
                    </div>
                    <span className="text-muted-foreground text-xs shrink-0">to</span>
                    <div className="flex-1 min-w-0">
                      <DatePickerField value={filterTo} onChange={setFilterTo} placeholder="To" />
                    </div>
                  </div>
                </div>

                {/* Type chips */}
                <div>
                  <p className="text-xs font-semibold text-muted-foreground mb-2">Type</p>
                  <div className="flex flex-wrap gap-2">
                    {availableTypes.length === 0 && (
                      <span className="text-xs text-muted-foreground">No types in current page</span>
                    )}
                    {availableTypes.map((t) => {
                      const isOn = selectedTypes.includes(t);
                      const badge = getTypeBadge(t);
                      return (
                        <button
                          key={t}
                          type="button"
                          onClick={() =>
                            setSelectedTypes((prev) =>
                              prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t],
                            )
                          }
                          className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                            isOn
                              ? "bg-foreground/10 text-foreground border-foreground/40 font-medium"
                              : "bg-transparent text-muted-foreground border-border hover:bg-muted"
                          }`}
                        >
                          {badge.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Brand multi-select — selection is by service_name since
                    that's what comes back per-row from the action. */}
                <div>
                  <p className="text-xs font-semibold text-muted-foreground mb-2">Brand</p>
                  <Popover open={brandPickerOpen} onOpenChange={setBrandPickerOpen}>
                    <PopoverTrigger
                      render={
                        <Button variant="outline" className="w-full justify-between font-normal" />
                      }
                    >
                      <span className="truncate">
                        {selectedBrandIds.length === 0
                          ? "All brands"
                          : selectedBrandIds.length === 1
                            ? selectedBrandIds[0]
                            : `${selectedBrandIds.length} brands selected`}
                      </span>
                      <ChevronsUpDown className="h-3.5 w-3.5 ml-2 shrink-0 text-muted-foreground" />
                    </PopoverTrigger>
                    <PopoverContent className="w-[280px] p-0" align="start">
                      <div className="max-h-[260px] overflow-auto py-1">
                        {availableBrands.length === 0 && (
                          <p className="text-xs text-muted-foreground px-3 py-2">No brands in current page</p>
                        )}
                        {availableBrands.map((name) => {
                          const isOn = selectedBrandIds.includes(name);
                          return (
                            <button
                              key={name}
                              type="button"
                              onClick={() =>
                                setSelectedBrandIds((prev) =>
                                  prev.includes(name) ? prev.filter((x) => x !== name) : [...prev, name],
                                )
                              }
                              className="w-full flex items-center justify-between text-sm px-3 py-1.5 hover:bg-muted text-left"
                            >
                              <span className="truncate">{name}</span>
                              {isOn && <Check className="h-4 w-4 shrink-0 text-primary" />}
                            </button>
                          );
                        })}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {hasActiveFilters && (
                <div className="flex justify-end">
                  <Button variant="ghost" size="sm" onClick={clearFilters} className="h-8">
                    <X className="h-3.5 w-3.5 mr-1.5" /> Clear filters
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </CollapsibleContent>
      </Collapsible>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Shimmer loading={loading} fallback={<TableShimmer cols={8} />}>
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-semibold">User</TableHead>
                  <TableHead className="font-semibold">Brand</TableHead>
                  <TableHead className="font-semibold">Type</TableHead>
                  <TableHead className="font-semibold">Purpose</TableHead>
                  <TableHead className="font-semibold text-right">Amount</TableHead>
                  <TableHead className="font-semibold text-right">Earnings</TableHead>
                  <TableHead className="font-semibold">Payment ID</TableHead>
                  <TableHead className="font-semibold">Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-12">
                      <span className="text-muted-foreground">
                        {search || hasActiveFilters ? "No transactions match your filters" : "No transactions yet"}
                      </span>
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((txn) => {
                    const badge = getTypeBadge(txn.type);
                    const isCredit = txn.amount > 0;
                    const phone = txn.auth.phone;
                    const displayId = txn.external_id ?? phone;
                    return (
                      <TableRow key={txn.id}>
                        <TableCell>
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">
                                {txn.auth.fullname}
                              </p>
                              <p className="text-xs text-muted-foreground font-mono truncate" title={displayId}>
                                {displayId}
                              </p>
                              {txn.external_id && (
                                <p className="text-[10px] text-muted-foreground font-mono truncate" title={phone}>
                                  internal: {phone.slice(0, 8)}…
                                </p>
                              )}
                            </div>
                            <Tooltip>
                              <TooltipTrigger
                                render={
                                  <button
                                    type="button"
                                    onClick={() => navigator.clipboard.writeText(displayId)}
                                    className="shrink-0 p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                                    aria-label="Copy user ID"
                                  />
                                }
                              >
                                <Copy className="h-3 w-3" />
                              </TooltipTrigger>
                              <TooltipContent>Copy full ID</TooltipContent>
                            </Tooltip>
                          </div>
                        </TableCell>
                        <TableCell>
                          {txn.service_name ? (
                            <div className="flex items-center gap-2">
                              <Avatar className="h-7 w-7 rounded-md">
                                {txn.service_image && (
                                  <AvatarImage src={txn.service_image} alt={txn.service_name} />
                                )}
                                <AvatarFallback className="text-[10px] rounded-md">
                                  {txn.service_name.slice(0, 2).toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                              <span className="text-sm">{txn.service_name}</span>
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={badge.className}>
                            {badge.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-muted-foreground">{txn.purpose}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={`text-sm font-semibold ${isCredit ? "text-green-600 dark:text-green-400" : "text-red-500 dark:text-red-400"}`}>
                            {isCredit ? "+" : "-"}{formatINR(txn.amount)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          {txn.earnings_paise != null ? (
                            <span className="text-sm font-semibold text-green-600 dark:text-green-400">
                              +{formatINR(txn.earnings_paise)}
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="text-xs text-muted-foreground font-mono">
                            {txn.payment_id ? (txn.payment_id.length > 16 ? txn.payment_id.slice(0, 16) + "..." : txn.payment_id) : "—"}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="text-xs text-muted-foreground">{formatDate(txn.created_at)}</span>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
            <DataTablePagination
              count={total}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={(p) => setPage(p)}
              onRowsPerPageChange={(rpp) => { setRowsPerPage(rpp); setPage(0); }}
            />
          </CardContent>
        </Card>
      </Shimmer>
    </div>
  );
}
